/**
 * GelBandControls Component
 *
 * Description:
 * This component provides UI controls to adjust the position of a gel band within a specified range.
 * Users can either click on the provided up and down arrows or use the drag functionality to move the gel band.
 *
 * Features:
 * - Arrow-based navigation: Allows users to move the gel band up or down.
 * - Drag functionality: Users can drag to adjust the gel band's position.
 * - Integration with Redux: Manages state changes using the Redux store.
 *
 * Import Dependencies:
 */
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { setGelPrediction } from "./store/actions";

// SVG assets for control arrows.
import Up from "./img/up.svg";
import UpDisabled from "./img/up-disabled.svg";
import Down from "./img/down.svg";
import DownDisabled from "./img/down-disabled.svg";

// Component-specific styles.
import styles from "./style.module.scss";

// Control images for various states.
const images = {
  up: Up,
  upDisabled: UpDisabled,
  down: Down,
  downDisabled: DownDisabled,
};

class GelBandControls extends React.Component {
  constructor(props) {
    super(props);
    this.dragAreaRef = React.createRef();
    this.dragStartY = 0;
  }

  componentDidMount() {
    // Add drag event listeners.
    this.dragAreaRef.current.addEventListener(
      "dragstart",
      this.handleDragStart
    );
    this.dragAreaRef.current.addEventListener("drag", this.handleDrag);
    this.dragAreaRef.current.addEventListener("dragend", this.handleDragEnd);
  }

  componentWillUnmount() {
    // Clean up event listeners to avoid memory leaks.
    this.dragAreaRef.current.removeEventListener(
      "dragstart",
      this.handleDragStart
    );
    this.dragAreaRef.current.removeEventListener("drag", this.handleDrag);
    this.dragAreaRef.current.removeEventListener("dragend", this.handleDragEnd);
  }

  /**
   * Drag Functionality:
   * The drag functionality allows users to adjust the gel band's position by dragging it.
   * This is achieved through a series of drag event listeners.
   */

  // Capture the initial Y-coordinate when the drag starts.
  handleDragStart = (e) => {
    this.dragStartY = e.clientY;
  };

  // Calculate the change in position as the user drags.
  handleDrag = (e) => {
    if (e.clientY !== 0) {
      const delta = e.clientY - this.dragStartY;
      this.move(this.props.position + delta);
      this.dragStartY = e.clientY;
    }
  };

  // Update the final position when the user releases the drag.
  handleDragEnd = () => {
    this.move(this.props.position);
  };

  // Update the gel band's position.
  move = (newPosition) => {
    newPosition = Math.max(0, Math.min(newPosition, 333));
    this.props.setGelPrediction({
      id: this.props.id,
      color: this.props.color,
      position: newPosition,
      type: this.props.type,
      lane: this.props.lane,
    });
  };

  render() {
    const { intl, component_strings } = this.props;
    return (
      <>
        {/* Up Arrow Control */}
        <img
          role="presentation"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              this.move(this.props.position - 3);
            }
          }}
          onClick={() => this.move(this.props.position - 3)}
          className={styles.UpArrow}
          src={this.props.position === 0 ? images.upDisabled : images.up}
          alt={intl.formatMessage(
            {
              id: component_strings.particle_alt_up,
              defaultMessage: component_strings.particle_alt_up,
              description: "",
            },
            {
              color: intl.formatMessage({
                id: component_strings[this.props.color.substring(1)],
                defaultMessage:
                  component_strings[this.props.color.substring(1)],
                description: "",
              }),
              position: 0,
            }
          )}
        />
        {/* Drag Area */}
        <div
          ref={this.dragAreaRef}
          draggable="true"
          className={styles.DragArea}
        />
        {/* Down Arrow Control */}
        <img
          role="presentation"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              this.move(this.props.position + 3);
            }
          }}
          onClick={() => this.move(this.props.position + 3)}
          className={styles.DownArrow}
          src={this.props.position >= 333 ? images.downDisabled : images.down}
          alt={intl.formatMessage(
            {
              id: component_strings.particle_alt_down,
              defaultMessage: component_strings.particle_alt_down,
              description: "",
            },
            {
              color: intl.formatMessage({
                id: component_strings[this.props.color.substring(1)],
                defaultMessage:
                  component_strings[this.props.color.substring(1)],
                description: "",
              }),
              position: 0,
            }
          )}
        />
      </>
    );
  }
}

// Prop type validation for the component.
GelBandControls.propTypes = {
  id: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  lane: PropTypes.number.isRequired,
  setGelPrediction: PropTypes.func.isRequired,
};

/**
 * Maps the state from the Redux store to the component props.
 * @param {object} state - The current state from the Redux store.
 * @param {object} ownProps - The props passed to the component.
 * @returns {object} - Returns the mapped props.
 */
function mapStateToProps(state, ownProps) {
  const gelData = state.gel[ownProps.type][ownProps.id];
  return gelData ? { ...gelData } : { ...ownProps };
}

// Connect the component to the Redux store.
export default connect(mapStateToProps, { setGelPrediction })(GelBandControls);
